import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { API_IDS } from '@intigral/constant';
import {
    IAddonPurchaseResponse,
    IItemsToPurchase,
    IAmazonActivateResponse,
    IBundleAndSaveParams,
    IBundleAndSaveResponse,
    CSRFTokenRequest,
    IBundleAndSaveInfoResponse,
    IBundleAndSaveInfoParams,
    IBundleAndSaveRulesResponse,
    IBundleAndSaveRulesParams,
} from '../../entity/interface/iSlpApis';
import { IAuthApisFormSigatureResponse } from '../../entity/interface/iAuthApis';
import { ISlpRepository } from '../../core/repositories/interfaces/iSlpRepository';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';

const __getFormSignature = (tokenName: string, token: string) => {
    const form: { [key: string]: string } = {};
    form[tokenName] = token;
    return form;
};

class SlpRepository implements ISlpRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get mlpHttp(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    /**
     * Get CSRF form signature
     * @returns {data: {token: string, tokenName: string}}
     */
    async getFormSignature(): Promise<IAuthApisFormSigatureResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORM_SIGNATURE);
        const response = await this.http.get<IAuthApisFormSigatureResponse>(path, {
            withCredentials: true,
        });
        return response.data;
    }

    async purchaseAddon(
        username: string,
        itemsToPurchase: IItemsToPurchase[],
        pin: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IAddonPurchaseResponse> {
        const path = this.apiPathManager.getPath(API_IDS.ENTITLEMENT_ADDON_PURCHASE);
        const response = await this.http.post<IAddonPurchaseResponse>(path, {
            withCredentials: true,
            body: {
                ...csrfToken,
                itemsToPurchase,
                pin,
                username,
                authToken,
                authSign,
                identityToken,
            },
        });

        return response.data;
    }

    /**
     * Verify QR code and get user auth object in response.
     * @param entitlementId
     * @returns activationUrl
     */
    async amazonActivate(entitlementId: string): Promise<IAmazonActivateResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_AMAZON_ACTIVATE);
        const response = await this.mlpHttp.get<IAmazonActivateResponse>(path, {
            withCredentials: true,
            params: {
                entitlementId,
            },
        });
        return response.data as IAmazonActivateResponse;
    }

    async bundleAndSave(payload: IBundleAndSaveParams): Promise<IBundleAndSaveResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_BUNDLE_SAVE);
        const response = await this.http.post<IBundleAndSaveResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
            },
        });
        return response.data;
    }

    async bundleAndSaveInfo(payload: IBundleAndSaveInfoParams, identityToken?: string, csrfToken?: CSRFTokenRequest): Promise<IBundleAndSaveInfoResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_BUNDLE_SAVE_INFO);
        const response = await this.http.post<IBundleAndSaveInfoResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
                identityToken: identityToken,
                ...csrfToken
            },
        });
        return response.data;
    }

    async bundleAndSaveRules(
        payload: IBundleAndSaveRulesParams,
        identityToken?: string
    ): Promise<IBundleAndSaveRulesResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_BUNDLE_SAVE_RULES);
        const response = await this.http.get<IBundleAndSaveRulesResponse>(path, {
            withCredentials: true,
            params: {
                ...payload,
            },
            headers: {
                ...(identityToken ? { identityToken: identityToken } : {}),
            },
        });
        return response.data;
    }
}

export default SlpRepository;
